import { DeleteClusterModal } from '@/components/Modals/DeleteClusterModal'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { Box, Button, Icon, Tooltip, useDisclosure } from '@chakra-ui/react'
import { Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type DeleteClusterButtonProps = Readonly<{
  /**
   * Organization ID for the cluster
   */
  orgId: string
  /**
   * Cluster ID to delete
   */
  clusterId: string
  /**
   * Display name for the cluster (optional, defaults to clusterId)
   */
  clusterName?: string
  /**
   * Whether the cluster is currently being deleted
   */
  isDeleting?: boolean
  /**
   * Callback function to execute when delete is confirmed
   */
  onDelete: (orgId: string, clusterId: string) => void
  /**
   * Optional tooltip description shown when the button is disabled
   */
  tooltipDescription?: string
}>

/**
 * Delete a cluster from a page header action bar.
 *
 * Same guarantees as `DeleteClusterDangerZone` — admin-only, name-confirmation
 * modal, disabled-with-reason tooltip — without the danger-zone card around
 * it. A whole card at the bottom of the page to hold one button pushed the
 * actual content up and read as a section of its own; the action belongs with
 * the page's other actions. The confirmation modal is what protects against an
 * accidental delete, not the card's framing.
 */
export function DeleteClusterButton({
  orgId,
  clusterId,
  clusterName,
  isDeleting = false,
  onDelete,
  tooltipDescription,
}: DeleteClusterButtonProps) {
  const { t } = useTranslation()
  const { open, onOpen, onClose } = useDisclosure()
  const { hasAdminRights } = useRBACPermissions()
  const isAdmin = hasAdminRights()

  function handleDelete() {
    onDelete(orgId, clusterId)
    onClose()
  }

  const isDisabled = !isAdmin || isDeleting

  const permissionWarning = isAdmin
    ? undefined
    : String(t('deleteCluster.dangerZone.adminTooltip'))
  const tooltipMessage = tooltipDescription || permissionWarning

  const button = (
    <Button
      variant="subtle"
      colorPalette="red"
      size="sm"
      disabled={isDisabled}
      onClick={onOpen}
      data-testid="dangerous-action-open"
    >
      <Icon as={Trash2} />
      {String(t('deleteCluster.dangerZone.buttonText'))}
    </Button>
  )

  return (
    <>
      {/* A disabled button swallows pointer events, so the tooltip has to hang
          off a wrapper element — otherwise the one case the tooltip exists for
          (explaining why it is disabled) is the one case it never shows. */}
      {isDisabled && tooltipMessage ? (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Box>{button}</Box>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              {tooltipMessage}
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      ) : (
        button
      )}

      <DeleteClusterModal
        clusterName={clusterName ?? clusterId}
        isOpen={open}
        onClose={onClose}
        handleDelete={handleDelete}
      />
    </>
  )
}
